import { useRef, useState, type ReactNode } from 'react';

import { Button } from '../core/Button';
import { Dialog, type DialogSize } from './Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Extra body content: the blast radius of the action. */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * The destructive action. Returning a promise keeps the dialog busy for its
   * duration. Closing the dialog stays the caller's decision — it owns `open` —
   * so a failed confirm can leave the dialog up with its error visible.
   */
  onConfirm: () => void | Promise<void>;
  /** Called before the dialog closes via Escape, the overlay, or Cancel. */
  onCancel?: () => void;
  /** Caller-controlled busy state; combined with the pending `onConfirm`. */
  loading?: boolean;
  size?: DialogSize;
}

/**
 * The destructive variant of `Dialog` — not a second modal.
 *
 * `Dialog` supplies `role="alertdialog"`, the red tone and the focus handling;
 * this file supplies the decision itself. Two things differ from a default
 * dialog, and both come from the spec for a confirmation:
 *
 * - focus lands on the destructive action on open (`initialFocus`), so a
 *   keyboard user is not handed a one-Tab-away accident. It is only requested
 *   while that button is enabled — focusing a disabled button is a no-op, which
 *   would silently drop focus onto the body.
 * - overlay press and Escape both cancel, which is Base UI's default for
 *   `Dialog` (`disablePointerDismissal` defaults to `false`,
 *   `@base-ui/react/dialog/root/DialogRoot.js`), so neither path can destroy
 *   anything.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
  size = 'sm',
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const [pending, setPending] = useState(false);
  const busy = loading || pending;

  function cancel() {
    onCancel?.();
    onOpenChange(false);
  }

  async function confirm() {
    if (busy) return;
    const result = onConfirm();
    if (result instanceof Promise) {
      setPending(true);
      try {
        await result;
      } finally {
        setPending(false);
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Any close that is not the confirm button is a cancellation.
        if (next) onOpenChange(true);
        else cancel();
      }}
      title={title}
      description={description}
      size={size}
      variant="confirm"
      tone="danger"
      loading={busy}
      initialFocus={busy ? undefined : confirmRef}
      hideClose
      footer={
        <>
          <Button variant="outline" size="sm" onClick={cancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant="danger"
            size="sm"
            loading={busy}
            loadingLabel={`${confirmLabel}…`}
            onClick={confirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children ? <div className="space-y-3">{children}</div> : null}
    </Dialog>
  );
}
